import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import { Box, Button, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';
import Markdown from '/@/components/markdown';
import { _ } from '/@/locale';
import BagQualityShare from './bag-quality-share';

const Row = ({
    label,
    value,
    tooltip,
    // In the PDF the metric descriptions are shown inline (gray text under the row)
    // instead of behind a hover info icon, matching the datasheet design.
    inlineDescription,
    children,
}: {
    label: string;
    value?: React.ReactNode;
    tooltip?: string;
    inlineDescription?: boolean;
    children?: React.ReactNode;
}) => {
    return (
        <Box>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Stack direction='row' alignItems='center' spacing={0.5}>
                    <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                        {label}
                    </Typography>
                    {tooltip && !inlineDescription && (
                        <Tooltip title={tooltip} arrow>
                            <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'pointer' }} color='primary' />
                        </Tooltip>
                    )}
                </Stack>
                {value !== undefined && (
                    <Typography variant='subtitle1' color='primary'>
                        {value}
                    </Typography>
                )}
            </Stack>
            {tooltip && inlineDescription && (
                <Box sx={{ maxWidth: '60%' }}>
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        {tooltip}
                    </Typography>
                </Box>
            )}
            {children}
        </Box>
    );
};

const BulletRow = ({
    label,
    value,
    tooltip,
    inlineDescription,
}: {
    label: string;
    value: React.ReactNode;
    tooltip?: string;
    inlineDescription?: boolean;
}) => (
    <Box component='li' sx={{ ml: 3, listStyle: 'disc', display: 'list-item' }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Stack direction='row' alignItems='center' spacing={0.5}>
                <Typography variant='subtitle1' color='primary'>
                    {label}
                </Typography>
                {tooltip && !inlineDescription && (
                    <Tooltip title={tooltip} arrow>
                        <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'pointer' }} color='primary' />
                    </Tooltip>
                )}
            </Stack>
            <Typography variant='subtitle1' color='primary'>
                {value}
            </Typography>
        </Stack>
        {tooltip && inlineDescription && (
            <Box sx={{ maxWidth: '60%' }}>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    {tooltip}
                </Typography>
            </Box>
        )}
    </Box>
);

const DAVContent = ({ response }: { response: Record<string, any> }) => {
    const dav = response?.dav;
    if (!dav || typeof dav !== 'string') {
        return null;
    }
    const davs = dav.split('/').filter(Boolean);
    if (davs.length === 0) {
        return null;
    }
    return (
        <Stack direction='row' spacing={1} alignItems='center'>
            {davs.map((davItem, idx) => (
                <Stack key={davItem || idx} direction='row' spacing={0.5} alignItems='center'>
                    <span>{davItem}</span>
                    {response?.davsUrls?.[davItem] && (
                        <LaunchIcon
                            sx={{ fontSize: 18, cursor: 'pointer' }}
                            color='primary'
                            onClick={() => window.open(response.davsUrls[davItem], '_blank')}
                        />
                    )}
                    {idx < davs.length - 1 && <span>/</span>}
                </Stack>
            ))}
        </Stack>
    );
};

/**
 * Presentational body of the seed-quality result. Shared by the interactive card
 * (with header actions / note field) and the server-rendered PDF (read-only).
 */
export const ResultCardContent = ({
    response,
    showDpsAnalysis,
    note,
    headerActions,
    descriptionsInline,
    children,
}: {
    response: Record<string, any>;
    showDpsAnalysis: boolean;
    note?: string | null;
    headerActions?: React.ReactNode;
    descriptionsInline?: boolean;
    children?: React.ReactNode;
}) => {
    const descriptions = useMemo(
        () => ({
            pg: _(
                'Análisis de laboratorio estandarizado donde se expone a la semilla de maíz a condición óptimas de temperatura, humedad y sustrato para estimar el máximo potencial de germinación del lote analizado.'
            ),
            vigor: _('Medida de la capacidad de la semilla para germinar y desarrollarse en condiciones adversas.'),
            dav: _(
                'Abreviatura del Documento de Autorización de Ventas emitido por INASE (instituto Nacional de las semillas)'
            ),
            recommendedPlate: _(
                'Es la placa de siembra que mejor  cumple con las especificaciones citadas en el manual DPS de referencia'
            ),
            vacuum: _(
                'Diferencia de presión generada por el sistema neumático de la sembradora que permite que las semillas se adhieran a los orificios del disco dosificador para ser transportadas y liberadas individualmente al surco.'
            ),
            target: _(
                'Aplicar la presión adecuada para que cada orificio del disco capture y transporte una única semilla, logrando alta singulación y minimizando fallas y duplicaciones.'
            ),
            flowRegulation: _(
                'Ajuste de la sembradora para entregar la cantidad de semillas necesaria para alcanzar la densidad de siembra objetivo (configuración del dosificador, transmisión o sistema electrónico de control).'
            ),
            feedingIndex: _(
                'Porcentaje de la cantidad de semillas que pasaron por cada orificio de la placa con respecto a la cantidad de orificios totales.'
            ),
            doubles: _('Porcentaje de la cantidad de semillas que pasaron en exceso por cada orificio.'),
            failures: _(
                'Porcentaje de la cantidad de semillas que no pasaron por cada orificio de la placa con respecto a la cantidad de orificios totales.'
            ),
        }),
        []
    );

    const has = (key: string) => {
        const v = response[key];
        return v !== null && v !== undefined && v !== '';
    };

    const dpsAnalysis = Array.isArray(response.dpsAnalysis) ? response.dpsAnalysis : [];

    return (
        <Box sx={{ border: (t) => `1px solid ${t.palette.divider}`, px: 2, py: 3 }}>
            <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
                <Typography variant='h6' color='primary'>
                    {_('Lote')} {response.batch}
                </Typography>
                {headerActions}
            </Stack>

            <Stack spacing={1}>
                {has('hybrid') && <Row label={_('Híbrido')} value={response.hybrid} />}
                {has('grade') && <Row label={_('Grado')} value={response.grade} />}
                {has('germinationPower') && (
                    <Row
                        label={_('PG (Poder Germinativo)')}
                        value={response.germinationPower}
                        tooltip={descriptions.pg}
                        inlineDescription={descriptionsInline}
                    />
                )}
                {has('vigor') && (
                    <Row
                        label={_('Vigor')}
                        value={response.vigor}
                        tooltip={descriptions.vigor}
                        inlineDescription={descriptionsInline}
                    />
                )}
                {has('dav') && (
                    <Row
                        label={_('DAV')}
                        value={<DAVContent response={response} />}
                        tooltip={descriptions.dav}
                        inlineDescription={descriptionsInline}
                    />
                )}
                {has('recommendedPlate') && (
                    <Row
                        label={_('Placa recomendada')}
                        value={response.recommendedPlate}
                        tooltip={descriptions.recommendedPlate}
                        inlineDescription={descriptionsInline}
                    >
                        <Box component='ul' sx={{ m: 0, mt: 0.5, pl: 0 }}>
                            {has('vacuum') && (
                                <BulletRow
                                    label={_('Vacío')}
                                    value={response.vacuum}
                                    tooltip={descriptions.vacuum}
                                    inlineDescription={descriptionsInline}
                                />
                            )}
                            {has('target') && (
                                <BulletRow
                                    label={_('Objetivo')}
                                    value={response.target}
                                    tooltip={descriptions.target}
                                    inlineDescription={descriptionsInline}
                                />
                            )}
                            {has('flowRegulation') && (
                                <BulletRow
                                    label={_('Regulación de caudal')}
                                    value={response.flowRegulation}
                                    tooltip={descriptions.flowRegulation}
                                    inlineDescription={descriptionsInline}
                                />
                            )}
                            {has('feedingIndex') && (
                                <BulletRow
                                    label={_('Índice de alimentación')}
                                    value={response.feedingIndex}
                                    tooltip={descriptions.feedingIndex}
                                    inlineDescription={descriptionsInline}
                                />
                            )}
                            {has('doubles') && (
                                <BulletRow
                                    label={_('% Dobles')}
                                    value={response.doubles}
                                    tooltip={descriptions.doubles}
                                    inlineDescription={descriptionsInline}
                                />
                            )}
                            {has('failures') && (
                                <BulletRow
                                    label={_('% Fallos')}
                                    value={response.failures}
                                    tooltip={descriptions.failures}
                                    inlineDescription={descriptionsInline}
                                />
                            )}
                        </Box>
                    </Row>
                )}
            </Stack>

            {showDpsAnalysis && dpsAnalysis.length > 0 && (
                <Box sx={{ background: '#F5F5F5', p: 2, mt: 2 }}>
                    {dpsAnalysis.map((el: any, index: number) => (
                        <Markdown
                            // biome-ignore lint/suspicious/noArrayIndexKey: stable list rendered from a single response
                            key={index}
                            components={{
                                strong: ({ children }) => (
                                    <Typography variant='subtitle1' color='primary' fontWeight='bold' component='span'>
                                        {children}
                                    </Typography>
                                ),
                                p: ({ children }) => (
                                    <Typography variant='subtitle1' color='primary'>
                                        {children}
                                    </Typography>
                                ),
                            }}
                        >
                            {`**${_('Análisis DPS:')}** ${el?.finishDate || '-'} - **${_('Laboratorio:')}** ${el?.laboratory || '-'} - **${_('PG (%):')}** ${el?.pgPercent ?? '-'}`}
                        </Markdown>
                    ))}
                </Box>
            )}

            {note && (
                <Typography variant='body2' color='primary' sx={{ mt: 2 }}>
                    {note}
                </Typography>
            )}

            {children}
        </Box>
    );
};

const ResultCard = ({
    data,
    note,
    onNoteChange,
    onNewSearch,
    onSave,
    canSave,
    showUser,
    showDpsAnalysis,
    savedNote,
    brand,
}: {
    data: { response?: Record<string, any> };
    note: string;
    onNoteChange: (value: string) => void;
    onNewSearch: () => void;
    onSave: () => void;
    canSave: boolean;
    showUser: boolean;
    showDpsAnalysis: boolean;
    savedNote: string | null;
    brand?: { id?: number; name?: string; logo?: string };
}) => {
    // Viewing a saved consultation (opened from the sidebar): show its note read-only.
    const viewingSaved = savedNote !== null;
    const response = data?.response ?? {};
    const currentNote = viewingSaved ? savedNote : note;

    return (
        <ResultCardContent
            response={response}
            showDpsAnalysis={showDpsAnalysis}
            note={viewingSaved ? savedNote : undefined}
            headerActions={
                <BagQualityShare
                    response={response}
                    showDpsAnalysis={showDpsAnalysis}
                    note={currentNote}
                    brand={brand}
                    // Sharing by email requires a logged-in user; downloading is public.
                    canShare={showUser}
                />
            }
        >
            {showUser &&
                (viewingSaved ? (
                    <Stack direction='row' justifyContent='flex-end' mt={2}>
                        <Button variant='outlined' onClick={onNewSearch}>
                            {_('Nueva búsqueda')}
                        </Button>
                    </Stack>
                ) : (
                    <>
                        <TextField
                            fullWidth
                            size='small'
                            value={note}
                            placeholder={_('Escribir una nota')}
                            onChange={(e) => onNoteChange(e.target.value)}
                            inputProps={{ maxLength: 20 }}
                            sx={{ mt: 2 }}
                        />
                        <Stack direction='row' spacing={1} justifyContent='flex-end' mt={2}>
                            <Button variant='outlined' onClick={onNewSearch}>
                                {_('Nueva búsqueda')}
                            </Button>
                            <Button variant='contained' onClick={onSave} disabled={!canSave}>
                                {_('Guardar')}
                            </Button>
                        </Stack>
                    </>
                ))}
        </ResultCardContent>
    );
};

export default ResultCard;
